import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import RawHeader from "../components/RawHeader";
import { InfoFooter } from "./InfoPages";
import { preOrderDeposit, preOrderBalance } from "../lib/preOrder";

const Shop = () => {
  const loc = useLocation();
  const nav = useNavigate();
  const qsGender = (new URLSearchParams(loc.search).get("gender") || "all").toLowerCase();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adExists, setAdExists] = useState(false);
  const [showAd, setShowAd] = useState(false);
  const gender = ["all", "men", "women"].includes(qsGender) ? qsGender : "all";

  // Resolve backend URL so the <video> tag can stream the MP4 directly (bypasses axios)
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const adVideoSrc = `${backendUrl}/api/public/ad-video/latest.mp4`;

  useEffect(() => {
    api.get("/public/ad-video/info")
      .then((r) => setAdExists(!!r.data?.exists))
      .catch(() => setAdExists(false));
  }, []);

  const setGender = (g) => {
    if (g === "all") nav("/", { replace: false });
    else nav(`/?gender=${g}`, { replace: false });
  };

  useEffect(() => {
    const qs = gender === "all"
      ? "/products?published_only=true"
      : `/products?published_only=true&gender=${gender}`;
    setLoading(true);
    api.get(qs)
      .then((r) => setProducts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [gender]);

  return (
    <div className="min-h-screen text-white">
      <RawHeader variant="public" />

      {/* Hero */}
      <section className="relative border-b border-zinc-800 overflow-hidden" data-testid="hero-section">
        <div className="grid-bg absolute inset-0 opacity-40" />
        <div className="relative px-6 lg:px-10 py-20 lg:py-32 max-w-[1600px] mx-auto">
          <div className="flex items-start gap-4 mb-6 text-[10px] tracking-[0.3em] uppercase text-zinc-500">
            <span>[ COLLECTION // 01 ]</span>
            <span className="flex-1 h-px bg-zinc-800 mt-2" />
            <span>{new Date().getFullYear()}</span>
          </div>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-9xl leading-[0.85] mb-8">
            <span className="text-[#d4af37]">RAW</span><br />
            <span className="text-black" style={{ WebkitTextStroke: "1px #d4af37" }}>COMPANY</span><br />
            <span className="text-[#d4af37]">CLOTHING</span>
          </h1>
          <p className="max-w-xl text-sm text-zinc-400 leading-relaxed mb-4">
            Premium UK streetwear with a gold standard. Bold designs, quality fabrics,
            and a look that turns heads. <span className="text-[#d4af37]">Wear the brand that builds.</span>
          </p>
          <p className="max-w-xl text-[10px] tracking-[0.3em] uppercase text-zinc-600 mb-8">
            // FACTORY-SPEC TRANSPARENCY · DESIGNED IN-HOUSE · DELIVERED RAW
          </p>
          <a href="#catalog" className="btn-raw btn-raw-primary" data-testid="hero-cta">
            → ENTER CATALOG
          </a>
          {adExists && (
            <button
              type="button"
              onClick={() => setShowAd(true)}
              data-testid="watch-advert-btn"
              className="ml-3 inline-flex items-center gap-2 px-6 py-3 border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors text-[11px] tracking-[0.3em] uppercase font-bold shadow-[0_0_24px_rgba(212,175,55,0.35)]"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#d4af37] opacity-60 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#d4af37]" />
              </span>
              ▶ WATCH ADVERT
            </button>
          )}
        </div>

        {/* Feature strip — surfaces the 4 pillars right below the hero */}
        <div className="relative border-t border-zinc-800/60" data-testid="feature-strip">
          <div className="max-w-[1600px] mx-auto grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-zinc-800/60">
            {[
              { label: "Premium Streetwear", copy: "Standout pieces built to last." },
              { label: "Signature Gold Aesthetic", copy: "The unmistakable RAW look." },
              { label: "Fast UK Delivery", copy: "From our door to yours, quickly." },
              { label: "Fresh Drops", copy: "New pieces added regularly." },
            ].map((f) => (
              <div key={f.label} className="px-5 py-6 lg:px-8 lg:py-7">
                <div className="text-[9px] tracking-[0.3em] uppercase text-[#d4af37] mb-2">// {f.label}</div>
                <div className="text-xs text-zinc-400 leading-relaxed">{f.copy}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scrolling marquee */}
        <div className="marquee border-t border-zinc-800 bg-black py-3">
          <div className="flex items-center gap-12 text-[10px] tracking-[0.4em] uppercase text-zinc-600 pr-12">
            {Array(2).fill(null).map((_, i) => (
              <React.Fragment key={i}>
                <span>Factory-Direct</span><span className="text-[#d4af37]">/</span>
                <span>Tech-Pack Engineered</span><span className="text-[#d4af37]">/</span>
                <span>Raw Cut Construction</span><span className="text-[#d4af37]">/</span>
                <span>Limited Runs</span><span className="text-[#d4af37]">/</span>
                <span>Global Shipping</span><span className="text-[#d4af37]">/</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Catalog */}
      <section id="catalog" className="max-w-[1600px] mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">// CATALOG</div>
            <h2 className="font-display text-3xl lg:text-4xl">DROP 001</h2>
          </div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
            {products.length} ITEM{products.length === 1 ? "" : "S"}
          </div>
        </div>

        {/* Gender filter chips */}
        <div data-testid="gender-filter" className="flex items-center gap-2 sm:gap-3 mb-10">
          {[
            { id: "all", label: "ALL" },
            { id: "men", label: "MEN" },
            { id: "women", label: "WOMEN" },
          ].map((opt) => (
            <button
              key={opt.id}
              data-testid={`filter-${opt.id}`}
              onClick={() => setGender(opt.id)}
              className={`px-5 sm:px-7 py-2.5 text-[11px] sm:text-[12px] tracking-[0.25em] uppercase font-bold border transition-colors ${
                gender === opt.id
                  ? "bg-[#d4af37] border-[#d4af37] text-black"
                  : "border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-black"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-32 text-zinc-600 text-xs tracking-[0.3em] uppercase">LOADING...</div>
        ) : products.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-16 text-center" data-testid="empty-shop">
            <div className="text-zinc-500 text-xs tracking-[0.3em] uppercase mb-4">// CATALOG EMPTY</div>
            <p className="text-zinc-600 text-sm mb-6">No products published yet. Admin must create designs.</p>
            <Link to="/admin/login" className="btn-raw">ADMIN LOGIN →</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-zinc-800" data-testid="product-grid">
            {products.map((p, idx) => (
              <Link
                to={`/product/${p.id}`}
                key={p.id}
                data-testid={`product-card-${p.id}`}
                className="bg-[#050505] group flex flex-col hover:bg-[#0a0a0a] transition-colors"
              >
                <div className="relative aspect-square bg-[#0a0a0a] overflow-hidden grid-bg">
                  {p.image_base64 ? (
                    <img
                      src={p.image_base64.startsWith("data:") ? p.image_base64 : `data:image/png;base64,${p.image_base64}`}
                      alt={p.name}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-zinc-700 text-xs tracking-[0.3em] uppercase">
                      NO IMAGE
                    </div>
                  )}
                  <div className="absolute top-3 left-3 flex items-center gap-2">
                    <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-mono">
                      #{String(idx + 1).padStart(3, "0")}
                    </div>
                    {p.gender && p.gender !== "unisex" && (
                      <div className="text-[9px] tracking-[0.3em] uppercase text-black bg-[#d4af37] font-mono px-1.5 py-0.5">
                        {p.gender === "men" ? "M" : "W"}
                      </div>
                    )}
                  </div>
                  <div className="absolute top-3 right-3 text-[9px] tracking-[0.3em] uppercase text-[#d4af37] font-mono border border-[#d4af37] px-2 py-0.5">
                    {p.clothing_type}
                  </div>
                  {typeof p.stock === "number" && p.stock > 0 && p.stock <= 3 && (
                    <div
                      data-testid={`low-stock-${p.id}`}
                      className="absolute bottom-3 left-3 text-[9px] tracking-[0.3em] uppercase font-mono text-black bg-[#d4af37] px-2 py-1 shadow-[0_0_12px_rgba(212,175,55,0.5)]"
                    >
                      ONLY {p.stock} LEFT
                    </div>
                  )}
                  {p.stock === 0 && (
                    <div
                      data-testid={`sold-out-${p.id}`}
                      className="absolute bottom-3 left-3 text-[9px] tracking-[0.3em] uppercase font-mono text-[#d4af37] border border-[#d4af37] bg-black/80 px-2 py-1"
                    >
                      SOLD OUT
                    </div>
                  )}
                </div>
                <div className="p-5 border-t border-zinc-800 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-display text-lg truncate">{p.name}</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1 truncate">
                      {p.material || "—"}
                    </div>
                    <div
                      data-testid={`preorder-badge-${p.id}`}
                      className="inline-block mt-2 px-2 py-0.5 text-[8px] tracking-[0.25em] uppercase bg-[#d4af37] text-black font-bold"
                    >
                      Pre-order · £{preOrderDeposit(p.price).toFixed(0)} deposit
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[8px] tracking-[0.2em] uppercase text-zinc-500">Deposit</div>
                    <div className="font-mono text-lg text-[#d4af37]">£{preOrderDeposit(p.price).toFixed(2)}</div>
                    <div className="text-[8px] tracking-[0.2em] uppercase text-zinc-600 mt-1">Full · £{Number(p.price).toFixed(2)}</div>
                    <div className="text-[8px] tracking-[0.2em] uppercase text-zinc-600">Balance · £{preOrderBalance(p.price).toFixed(2)}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <InfoFooter />

      {/* Advert modal */}
      {showAd && (
        <div
          data-testid="advert-modal"
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={() => setShowAd(false)}
        >
          <div
            className="relative w-full max-w-5xl bg-black border border-[#d4af37] shadow-[0_0_48px_rgba(212,175,55,0.35)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#d4af37]/40">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] font-mono">
                // RAW COMPANY // CINEMATIC ADVERT
              </div>
              <button
                type="button"
                data-testid="close-advert-btn"
                onClick={() => setShowAd(false)}
                className="text-[#d4af37] text-xl leading-none hover:text-white transition-colors"
                aria-label="Close advert"
              >
                ✕
              </button>
            </div>
            <video
              key={adVideoSrc}
              data-testid="advert-video"
              src={adVideoSrc}
              controls
              autoPlay
              playsInline
              className="w-full max-h-[75vh] bg-black"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Shop;
